import { useMemo, useState } from "react";
import { addMonths, format, isSameMonth } from "date-fns";
import { Copy, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";

const TYPE_EMOJI = {
  cup: "🏆",
  challenge: "⚔️",
  friendly: "🤝",
  prerelease: "✨",
  other: "🎲",
};

const DIVIDER = "━━━━━━━━━━━━━━";

const TEMPLATES = [
  { value: "roundup", label: "Monthly round-up" },
  { value: "compact", label: "Compact list" },
];

function buildRoundup(monthLabel, events) {
  const lines = [
    `🎴 *HEX LABS GAMES*`,
    `*${monthLabel.toUpperCase()} EVENTS*`,
    "",
    `Here's everything on at the store this month 👇`,
    "",
  ];
  events.forEach((e) => {
    lines.push(DIVIDER);
    lines.push(`📅 *${format(new Date(e.start), "EEE d MMM")}* · ${format(new Date(e.start), "HH:mm")}`);
    lines.push(`${TYPE_EMOJI[e.event_type] || "🎲"} *${e.title}*`);
    if (e.game) lines.push(`🃏 ${e.game}`);
    if (e.sold_out) lines.push(`🚫 _SOLD OUT_`);
    else if (e.ticket_url) lines.push(`🎟️ ${e.price ? `${e.price} — ` : ""}${e.ticket_url}`);
    else if (e.price) lines.push(`🎟️ ${e.price} — pay in store`);
    lines.push("");
  });
  lines.push(DIVIDER);
  lines.push("");
  lines.push("📍 1a Edward Bright Close, Maldon CM9 5RU");
  lines.push("📞 01621 488906");
  lines.push("🗓️ Full calendar: https://events.hexlabsgames.com");
  lines.push("");
  lines.push("_Tickets are limited — grab yours early!_ 🔥");
  return lines.join("\n");
}

function buildCompact(monthLabel, events) {
  const lines = [`🎴 *Hex Labs Games — ${monthLabel}*`, ""];
  events.forEach((e) => {
    const when = format(new Date(e.start), "EEE d MMM HH:mm");
    const tail = e.sold_out ? "🚫 SOLD OUT" : e.ticket_url || (e.price ? `${e.price} in store` : "");
    lines.push(`${TYPE_EMOJI[e.event_type] || "🎲"} *${e.title}*`);
    lines.push(`   ${when}${tail ? ` · ${tail}` : ""}`);
  });
  lines.push("");
  lines.push("🗓️ https://events.hexlabsgames.com");
  return lines.join("\n");
}

const MEDALS = ["🥇", "🥈", "🥉"];
const DIVISION_LABEL = { junior: "Juniors", senior: "Seniors", masters: "Masters" };

function buildResults(tournament) {
  if (!tournament) return "Select a tournament to build a results message.";
  const players = [...(tournament.players || [])].sort((a, b) => b.points - a.points);
  const top = players.slice(0, 3);
  const lines = [
    `🏆 *RESULTS*`,
    `*${tournament.name}*`,
    `📅 ${format(new Date(tournament.date), "d MMMM yyyy")} · ${tournament.player_count} players · ${tournament.round_count} round${tournament.round_count === 1 ? "" : "s"}`,
    "",
  ];
  top.forEach((p, i) => {
    lines.push(`${MEDALS[i]} *${p.name}* — ${p.points} pts (${p.wins}-${p.draws}-${p.losses})`);
  });

  const divisionWinners = ["junior", "senior", "masters"]
    .map((d) => [d, players.find((p) => p.division === d)])
    .filter(([, p]) => p);
  if (divisionWinners.length > 1) {
    lines.push("");
    lines.push(`*Division winners*`);
    divisionWinners.forEach(([d, p]) => lines.push(`▪️ ${DIVISION_LABEL[d]}: *${p.name}* — ${p.points} pts`));
  }

  lines.push("");
  if (!tournament.ranked) lines.push("_Prerelease event — no ranking points awarded._");
  else lines.push("_Points are now live on the leaderboard!_");
  lines.push("");
  lines.push("👏 Well played everyone!");
  lines.push("🗓️ Standings & next events: https://events.hexlabsgames.com/leaderboard");
  return lines.join("\n");
}

export function WhatsAppComposer({ events, tournaments = [] }) {
  const [mode, setMode] = useState("events");
  const [offset, setOffset] = useState(0);
  const [template, setTemplate] = useState("roundup");
  const [excluded, setExcluded] = useState([]);
  const [tournamentId, setTournamentId] = useState("");

  const cursor = addMonths(new Date(), offset);
  const monthLabel = format(cursor, "MMMM yyyy");

  const monthEvents = useMemo(
    () =>
      (events || [])
        .filter((e) => isSameMonth(new Date(e.start), cursor))
        .sort((a, b) => new Date(a.start) - new Date(b.start)),
    [events, cursor]
  );

  const included = monthEvents.filter((e) => !excluded.includes(e.id));

  const selectedTournament = tournaments.find((t) => t.id === (tournamentId || tournaments[0]?.id));

  const message = useMemo(() => {
    if (mode === "results") return buildResults(selectedTournament);
    if (included.length === 0) return `No events found for ${monthLabel}.`;
    return template === "roundup" ? buildRoundup(monthLabel, included) : buildCompact(monthLabel, included);
  }, [mode, selectedTournament, included, monthLabel, template]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(message);
      toast.success("Message copied — paste it straight into WhatsApp");
    } catch {
      const el = document.getElementById("whatsapp-message");
      el?.select();
      document.execCommand("copy");
      toast.success("Message copied");
    }
  };

  return (
    <div data-testid="whatsapp-composer">
      <div className="flex flex-wrap gap-1 mb-5" data-testid="whatsapp-mode-toggle">
        {[
          { value: "events", label: "Event line-up" },
          { value: "results", label: "Tournament results" },
        ].map((m) => (
          <button
            key={m.value}
            type="button"
            data-testid={`whatsapp-mode-${m.value}`}
            onClick={() => setMode(m.value)}
            className={`text-[11px] uppercase tracking-wider px-3 py-1.5 rounded-full border transition-colors duration-200 ${
              mode === m.value ? "border-primary text-primary bg-primary/10" : "border-white/10 text-neutral-500 hover:text-white"
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>

      {mode === "results" ? (
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[260px]">
            <Label className="text-xs uppercase tracking-wider text-neutral-400">Tournament</Label>
            <select
              data-testid="whatsapp-tournament-select"
              value={tournamentId || tournaments[0]?.id || ""}
              onChange={(e) => setTournamentId(e.target.value)}
              className="mt-1.5 h-10 px-3 w-full rounded-sm bg-[#121212] border border-white/10 text-sm block"
            >
              {tournaments.length === 0 && <option value="">No tournaments imported yet</option>}
              {tournaments.map((t) => (
                <option key={t.id} value={t.id}>
                  {format(new Date(t.date), "d MMM yyyy")} — {t.name}
                </option>
              ))}
            </select>
          </div>
          <Button
            onClick={copy}
            data-testid="whatsapp-copy-btn-results"
            className="bg-primary text-black font-bold uppercase tracking-wider rounded-full hover:bg-[#FFA94D]"
          >
            <Copy className="h-4 w-4 mr-2" /> Copy message
          </Button>
          <a href={`https://wa.me/?text=${encodeURIComponent(message)}`} target="_blank" rel="noreferrer">
            <Button variant="outline" className="rounded-full border-white/20 hover:border-primary hover:text-primary">
              <MessageCircle className="h-4 w-4 mr-2" /> Open in WhatsApp
            </Button>
          </a>
        </div>
      ) : (
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs uppercase tracking-wider text-neutral-400">Month</Label>
          <select
            data-testid="whatsapp-month-select"
            value={offset}
            onChange={(e) => { setOffset(Number(e.target.value)); setExcluded([]); }}
            className="mt-1.5 h-10 px-3 rounded-sm bg-[#121212] border border-white/10 text-sm block"
          >
            {[-1, 0, 1, 2, 3].map((o) => (
              <option key={o} value={o}>{format(addMonths(new Date(), o), "MMMM yyyy")}</option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-neutral-400">Style</Label>
          <select
            data-testid="whatsapp-template-select"
            value={template}
            onChange={(e) => setTemplate(e.target.value)}
            className="mt-1.5 h-10 px-3 rounded-sm bg-[#121212] border border-white/10 text-sm block"
          >
            {TEMPLATES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <Button
          onClick={copy}
          data-testid="whatsapp-copy-btn"
          className="bg-primary text-black font-bold uppercase tracking-wider rounded-full hover:bg-[#FFA94D]"
        >
          <Copy className="h-4 w-4 mr-2" /> Copy message
        </Button>
        <a
          href={`https://wa.me/?text=${encodeURIComponent(message)}`}
          target="_blank"
          rel="noreferrer"
          data-testid="whatsapp-open-link"
        >
          <Button variant="outline" className="rounded-full border-white/20 hover:border-primary hover:text-primary">
            <MessageCircle className="h-4 w-4 mr-2" /> Open in WhatsApp
          </Button>
        </a>
      </div>
      )}

      {mode === "events" && monthEvents.length > 0 && (
        <div className="mt-5">
          <Label className="text-xs uppercase tracking-wider text-neutral-400">Include events</Label>
          <div className="mt-2 flex flex-wrap gap-2" data-testid="whatsapp-event-toggles">
            {monthEvents.map((e) => {
              const on = !excluded.includes(e.id);
              return (
                <button
                  key={e.id}
                  type="button"
                  data-testid={`whatsapp-toggle-${e.id}`}
                  onClick={() =>
                    setExcluded((x) => (on ? [...x, e.id] : x.filter((id) => id !== e.id)))
                  }
                  className={`text-[11px] px-3 py-1.5 rounded-full border transition-colors duration-200 ${
                    on ? "border-primary text-primary bg-primary/10" : "border-white/10 text-neutral-500"
                  }`}
                >
                  {format(new Date(e.start), "d MMM")} · {e.title}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-6">
        <Label className="text-xs uppercase tracking-wider text-neutral-400">
          {mode === "results" ? "Preview" : `Preview (${included.length} event${included.length === 1 ? "" : "s"})`}
        </Label>
        <Textarea
          id="whatsapp-message"
          data-testid="whatsapp-message-preview"
          value={message}
          readOnly
          rows={18}
          className="mt-1.5 bg-[#0b141a] border-white/10 font-mono text-[13px] leading-relaxed scrollbar-thin"
        />
        <p className="text-[11px] text-neutral-500 mt-2">
          WhatsApp turns *stars* into bold and _underscores_ into italics automatically — paste it as-is.
        </p>
      </div>
    </div>
  );
}

export default WhatsAppComposer;
